import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MarketingPageHeader from "@/components/MarketingPageHeader";

const CommunityGuidelines = () => {
  return (
  <div className="min-h-screen bg-background">
    <Header />
    <MarketingPageHeader
      title="Community Guidelines"
      subtitle="Jynio is built on mutual respect between students and tutors. These guidelines help keep our community safe, welcoming, and productive for everyone."
      containerClassName="max-w-3xl"
    />
    <main className="container mx-auto px-4 pb-24 pt-6 max-w-3xl">
      <div className="prose text-muted-foreground space-y-4 text-sm">
        <p>Last updated: October 2026</p>

        <h2 className="text-lg font-semibold text-foreground mt-6">1. Be Respectful</h2>
        <p>
          Treat every member of the community with courtesy and professionalism. Harassment,
          discrimination, hate speech, or bullying of any kind will not be tolerated.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">2. Keep It Professional</h2>
        <p>
          Sessions are for language learning. Stay on topic, arrive on time, and come prepared.
          Tutors and students should communicate clearly about expectations and lesson goals.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">3. Protect Privacy</h2>
        <p>
          Do not share personal contact information, social media accounts, or payment details
          outside of the platform. Never record a session without the other person's explicit consent.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">4. Honest Profiles & Reviews</h2>
        <p>
          Keep your profile accurate and up to date. Leave honest, constructive reviews after
          sessions. Fake reviews or misleading credentials undermine trust and may result in
          account suspension.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">5. No Off-Platform Transactions</h2>
        <p>
          All bookings and payments must go through Jynio. Arranging sessions or payments
          outside the platform violates our terms and removes the protections we provide to
          both parties.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">6. Report Issues</h2>
        <p>
          If you experience or witness behaviour that violates these guidelines, please report
          it to our team at hello@jynio.co. We review every report and take appropriate action.
        </p>
        <p>
          If you or someone else is in immediate danger, call your local emergency number first
          (112 in the EU, 17 in France). We report threats to someone's life or safety to the
          police, as the law requires.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">7. Consequences</h2>
        <p>
          Depending on how serious it is, we remove what broke the rules, or restrict or close an
          account. A person at Jynio decides, and tells you what we did, which rule applies and how
          to contest it. The details are in{" "}
          <a href="/terms#how-we-moderate" className="underline">Section 17 of our Terms</a>.
        </p>

        {/* The tutor community's rules (#1196). Every rule has its own anchor so a notice can cite
            and link the exact rule; spec/frontend/tutor_community_legal_spec.rb pins this text to
            Community::RULES_VERSION. */}
        <section id="tutor-community" className="scroll-mt-24 space-y-4">
          <h2 className="text-lg font-semibold text-foreground mt-6">8. Tutor community</h2>
          <p>
            The tutor community is a space inside Jynio where tutors ask each other questions, share
            tips and hear from the Jynio team. Only tutors whose profile Jynio has approved, and
            hasn't switched off, can open it, while their account is active. Students can't see it.
            You post under your profile name and photo, and posts marked "Jynio team" are written by
            our staff. Sections 1 to 7 apply here too, and so do these rules:
          </p>
          <ul className="list-none space-y-3 pl-0">
            <li id="rule-8-1" className="scroll-mt-24">
              <strong className="text-foreground">8.1 Protect your students.</strong> Never post
              anything that could identify a student: no names or usernames, photos, contact details,
              or screenshots of chats, lessons or calendars, and nothing about their health or
              learning difficulties. Describe the situation instead.
            </li>
            <li id="rule-8-2" className="scroll-mt-24">
              <strong className="text-foreground">8.2 Keep students and payments on Jynio.</strong>{" "}
              Don't use the community to win other tutors' students, don't offer lessons or payments
              outside Jynio, and don't put contact details in a post, yours included.
            </li>
            <li id="rule-8-3" className="scroll-mt-24">
              <strong className="text-foreground">8.3 No selling or advertising,</strong> and no
              recruiting for other platforms or services.
            </li>
            <li id="rule-8-4" className="scroll-mt-24">
              <strong className="text-foreground">8.4 No politics or religion.</strong>
            </li>
            <li id="rule-8-5" className="scroll-mt-24">
              <strong className="text-foreground">8.5 Respect other members' privacy.</strong> Don't
              share screenshots of other members' posts, or their names and photos, outside the
              community.
            </li>
            <li id="rule-8-6" className="scroll-mt-24">
              <strong className="text-foreground">8.6 This isn't support.</strong> For a question
              about your account, a payout or a lesson, message support in Messages, where we can see
              your account.
            </li>
            <li id="rule-8-7" className="scroll-mt-24">
              <strong className="text-foreground">8.7 Reporting.</strong> Use "Report" on any post or
              reply. Only Jynio's moderators see reports, and the author is never told who sent one.
              If someone may be in danger, call your local emergency number first, then report it and
              choose "Someone may be in danger".
            </li>
            <li id="rule-8-8" className="scroll-mt-24">
              <strong className="text-foreground">8.8 What we do.</strong> Posts appear straight
              away, and the Jynio team reads them. The one exception: a post or reply that mentions a
              student's diagnosis or learning difficulty is held until a person at Jynio has looked at
              it, and its author sees that it is waiting. We pin useful posts, close threads that are
              finished or repeated, and mark where a reported problem or an idea stands. When
              something breaks these rules, a person at Jynio decides what to do: remove the post or
              reply, or remove its author from the community for a set time or with no end date,
              without touching their profile, lessons, students or pay. We tell the author what we
              did, which rule applies, the facts and how to contest it, and we tell whoever reported
              it what we decided. If we got it wrong, we put it back and say so. We keep the text of a
              deleted post, and the earlier versions of one that was edited, for our staff only, for
              one year, and longer only while a report or a removal relies on it. It is erased at once
              if your account is erased.
            </li>
          </ul>
        </section>
      </div>
    </main>
    <Footer />
  </div>
  );
};

export default CommunityGuidelines;
